import classNames from 'classnames';
import { ButtonHTMLAttributes, cloneElement, FC, Fragment } from 'react';
import { twMerge } from 'tailwind-merge';
import Spinner from '../spinner';
import { ButtonSize, ButtonStyle, ButtonVariant } from './buttonConfig';

/**
 * A button is used to react to interactions that trigger
 * an a function call, i.e., saving a form.
 *
 * For purely navigating, the `<Link />` component should be used.
 *
 * Example usage:
 *
 * ```tsx
 * <Button
 *  label={t("Delete")}
 *  variant="PRIMARY"
 *  buttonStyle="DANGER"
 *  size="SMALL"
 *  isLoading={isDeleting}
 *  onClick={deleteForm}
 * />
 * ```
 */
const Button: FC<
  {
    /**
     * The button label
     */
    label?: string;
    /**
     * Should be an imported component from '@mui/icons-material'
     */
    iconLeft?: JSX.Element;
    iconRight?: JSX.Element;
    variant?: ButtonVariant;
    /**
     * Naming this "buttonStyle" and not simply "style" to not clash with the HTML button prop
     */
    buttonStyle?: ButtonStyle;
    size?: ButtonSize;
    isLoading?: boolean;
    className?: string;
    dataTestId?: string;
  } & ButtonHTMLAttributes<HTMLButtonElement>
> = ({
  label,
  iconLeft,
  iconRight,
  variant = 'PRIMARY',
  buttonStyle = 'DEFAULT',
  size = 'LARGE',
  isLoading,
  className,
  dataTestId,
  ...props
}) => {
  const iconOnly = (iconLeft || iconRight) && !label;

  return (
    <button
      className={twMerge(
        classNames(
          'w-fit h-fit rounded-radius-02 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed',
          {
            /**
             * Padding
             */
            'px-spacing-05 py-spacing-03': size === 'LARGE' && !iconOnly,
            'px-spacing-04 py-spacing-02': size === 'MEDIUM' && !iconOnly,
            'px-spacing-03 py-spacing-02': size === 'SMALL' && !iconOnly,
            'px-spacing-02 py-spacing-01': size === 'XSMALL' && !iconOnly,
            'p-spacing-03': size === 'LARGE' && iconOnly,
            'p-spacing-02': ['MEDIUM', 'SMALL'].includes(size) && iconOnly,
            'p-spacing-01': size === 'XSMALL' && iconOnly,

            /**
             * Size
             */
            'h-12 w-[88px]': size === 'LARGE' && isLoading, // The order here is important to overwrite preceeding sizes
            'h-10 w-[72px]': size === 'MEDIUM' && isLoading, // The order here is important to overwrite preceeding sizes
            'h-8 w-16': size === 'SMALL' && isLoading, // The order here is important to overwrite preceeding sizes
            'h-6 w-14': size === 'XSMALL' && isLoading, // The order here is important to overwrite preceeding sizes

            /**
             * Variant
             */
            'text-inverse focus:ring-2 focus:ring-blue-800 focus:ring-offset-2':
              variant === 'PRIMARY',
            'outline-solid outline-2 -outline-offset-2 focus:outline-hidden focus:ring-3 focus:ring-blue-800 focus:ring-offset-2':
              variant === 'SECONDARY',

            /**
             * Button style
             */
            // With variant "PRIMARY"
            'bg-blue-600': variant === 'PRIMARY' && buttonStyle === 'DEFAULT',
            'bg-red-700': variant === 'PRIMARY' && buttonStyle === 'DANGER',
            // Hovering and active
            'hover:bg-blue-700 active:bg-blue-800':
              variant === 'PRIMARY' && !isLoading && buttonStyle === 'DEFAULT',
            'hover:bg-red-800 active:bg-red-900':
              variant === 'PRIMARY' && !isLoading && buttonStyle === 'DANGER',

            // With variant "GHOST"
            'text-interactive':
              variant === 'GHOST' && buttonStyle === 'DEFAULT',
            'text-red-700': variant === 'GHOST' && buttonStyle === 'DANGER',
            // Hovering and active
            'hover:bg-gray-100 active:bg-gray-200 focus:ring-3 focus:ring-blue-800 focus:ring-offset-2':
              variant === 'GHOST' && !isLoading,

            // With variant "SECONDARY"
            'text-interactive outline-blue-600':
              variant === 'SECONDARY' && buttonStyle === 'DEFAULT',
            'text-red-700 outline-red-700':
              variant === 'SECONDARY' && buttonStyle === 'DANGER',
            // Hovering and active
            'hover:outline-blue-700 active:outline-blue-800 hover:text-blue-700 active:text-blue-800':
              variant === 'SECONDARY' &&
              !isLoading &&
              buttonStyle === 'DEFAULT',
            'hover:outline-red-800 active:outline-red-900 hover:text-red-800 active:text-red-900':
              variant === 'SECONDARY' && !isLoading && buttonStyle === 'DANGER'
          }
        ),
        className
      )}
      data-test-id={dataTestId}
      disabled={props.disabled || isLoading}
      {...props}
    >
      <span className="flex flex-row items-center justify-center">
        {!isLoading && (
          <Fragment>
            {iconLeft && (
              <span className="flex h-fit">
                {cloneElement(iconLeft, {
                  sx: { fontSize: ['MEDIUM', 'LARGE'].includes(size) ? 20 : 16 }
                })}
              </span>
            )}

            {label && (
              <span
                className={classNames('whitespace-nowrap', {
                  'label-01 px-spacing-01 py-[2px]': [
                    'MEDIUM',
                    'LARGE'
                  ].includes(size),
                  'label-02 font-normal px-[2px]': ['SMALL', 'XSMALL'].includes(
                    size
                  )
                })}
              >
                {label}
              </span>
            )}

            {iconRight && (
              <span className="flex h-fit">
                {cloneElement(iconRight, {
                  sx: { fontSize: ['MEDIUM', 'LARGE'].includes(size) ? 20 : 16 }
                })}
              </span>
            )}
          </Fragment>
        )}

        {isLoading && (
          <Spinner
            size={['MEDIUM', 'LARGE'].includes(size) ? 'SMALL' : 'XSMALL'}
            color={buttonStyle === 'DEFAULT' ? 'BLUE' : 'GREY'}
          />
        )}
      </span>
    </button>
  );
};

export default Button;
